import Dinero from 'dinero.js'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import upperFirst from 'lodash/upperFirst'
import identity from 'lodash/identity'
import { Button } from '@farewill/ui'
import { type Invoice } from 'lib/models/invoice'
import { showModal } from 'state/actions'

import EstatePersonModal from './estate-person-modal'
import { relationshipLabel } from '../estate-questionnaire/options'

import {
  APPLICATION_TYPE_OPTIONS,
  TAX_FORM_OPTIONS,
  VAT_RATE,
} from '../../../../utils/enums'
import { ProbateCaseAttributes } from 'lib/models/probate-case'
import { type DisbursementBreakdownItem } from 'lib/models/probate-quote-history'
import {
  ProbateEstate,
  ProbateEstatePerson,
  ProbateEstatePeopleAttributes,
} from 'lib/models/probate-estate'
import { Lead } from 'lib/models/lead'
import { Dispatch } from 'redux'
import { Contact } from 'lib/models/contact'

export const StyledContactSentenceButton = styled(Button.Underline)`
  padding: 0;
`

export const getFullName = (
  firstName: string | null,
  lastName: string | null
) => {
  return [firstName, lastName].filter(identity).join(' ')
}

export const isNotAuthorizedToBeIncludedInComms = (
  person: ProbateEstatePeopleAttributes
) => {
  const isIncludedInCommsOnly =
    person.isIncludedInComms &&
    !person.isCustomer &&
    !person.isPersonalRepresentative &&
    !person.isPointOfContact

  return isIncludedInCommsOnly && !person.authorizedToBeIncludedInComms
}

const createContactString = (person: ProbateEstatePeopleAttributes) => {
  const contactString = []

  if (person.isCustomer) contactString.push('customer')
  if (person.personalRepresentativeType === 'executor')
    contactString.push('executor')
  if (person.isPointOfContact) contactString.push('point of contact')
  if (person.isPersonalRepresentative)
    contactString.push('personal representative')
  if (person.isIncludedInComms) contactString.push('include in all comms')

  return upperFirst(contactString.join(' and '))
}

export const getContactSentence = (
  dispatch: Dispatch,
  person: ProbateEstatePeopleAttributes,
  deceasedFirstName: string | null
) => {
  /**
   * [1] For some (unknown) reason executor's have `isPersonalRepresentative` =
   *     `false` & `personalRepresentativeType` === `executor`
   */
  if (
    !person.isCustomer &&
    !person.isPointOfContact &&
    !person.isPersonalRepresentative &&
    !person.personalRepresentativeType && // [1]
    !person.isIncludedInComms
  )
    return []

  const contactSentence = []

  contactSentence.push(`${createContactString(person)}: `)

  contactSentence.push(
    <StyledContactSentenceButton
      size="S"
      type="button"
      onClick={() =>
        dispatch(
          showModal({
            component: EstatePersonModal,
            headingText:
              getFullName(person.firstNames, person.lastName) ||
              'No name provided',
            config: { person },
          })
        )
      }
    >
      {isNotAuthorizedToBeIncludedInComms(person) && '🚨'}
      {getFullName(person.firstNames, person.lastName) || 'No name provided'}.
    </StyledContactSentenceButton>
  )

  if (person.relationshipToDeceased) {
    const { customRelationshipToDeceased, relationshipToDeceased } = person

    const relationship =
      customRelationshipToDeceased ||
      relationshipLabel(relationshipToDeceased)?.toLowerCase()

    if (relationshipToDeceased === 'other' && !customRelationshipToDeceased) {
      contactSentence.push(' (No relationship specified)')
    } else {
      contactSentence.push(
        ` ${person.firstNames} is ${deceasedFirstName}’s ${relationship}.`
      )
    }
  }

  if (person.isActingOnBehalfOfSomeoneElse) {
    contactSentence.push(
      ` ${person.firstNames} is acting on behalf of someone else.`
    )
  }

  return contactSentence
}

export const getProbateEstateDetails = (
  probateEstate: ProbateEstate,
  probateCase: ProbateCaseAttributes
) => {
  if (!probateEstate) return []

  const {
    probateCaseId,
    hasWill,
    hasForeignWills,
    hasCodicils,
    willIsUndatedOrUnsigned,
    willIsUnbound,
    willHasIncorrectStatementsAboutWitnesses,
    willHasTrusts,
    willIsDiy,
    willHasShakySignature,
    willHasTearsOrMarks,
    willFeelsUnsigned,
    willHasHandwrittenAmendments,
    willHasUninitialledAmendments,
    willHasInContemplationOfMarriageClause,
    willHasIssuesWithCondition,
    willHasCodicilReplacingExecutors,
    wasAdopted,
  } = probateEstate.attributes

  const {
    taxForm,
    estateAdministrationRequired,
    applicationTypeSubmitted,
    applicationReferenceNumber,
    hasError,
  } = probateCase

  const probateEstateDetails = []

  const willOrIntestacy = hasWill
    ? 'Will'
    : hasWill === false
      ? 'Intestacy'
      : null

  const taxFormType = TAX_FORM_OPTIONS.find(
    (option) => option.value === taxForm
  )?.label

  const grantOrEstateAdmin = estateAdministrationRequired
    ? 'Complete Probate'
    : 'Essential Probate'

  const applicationType = APPLICATION_TYPE_OPTIONS.find(
    (option) => option.value === applicationTypeSubmitted
  )?.label

  if (willOrIntestacy)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/fact-find#2`}>
        {willOrIntestacy}
      </Link>
    )
  if (hasForeignWills === 'yes')
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/fact-find#2`}>
        Foreign will
      </Link>
    )
  if (hasCodicils === 'yes')
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/fact-find#2`}>
        Codicil
      </Link>
    )
  if (willIsUndatedOrUnsigned)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Undated / unsigned
      </Link>
    )
  if (willIsUnbound)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Unbound
      </Link>
    )
  if (willHasIncorrectStatementsAboutWitnesses)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Incorrect statements about witnesses
      </Link>
    )
  if (willHasTrusts)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Trusts in will
      </Link>
    )
  if (willIsDiy)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        DIY will
      </Link>
    )
  if (willHasShakySignature)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Shaky signature
      </Link>
    )
  if (willHasTearsOrMarks)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Tears or marks
      </Link>
    )
  if (willFeelsUnsigned)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Feels unsigned
      </Link>
    )
  if (willHasHandwrittenAmendments)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Handwritten amendments
      </Link>
    )
  if (willHasUninitialledAmendments)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Amendments not initialled
      </Link>
    )
  if (willHasInContemplationOfMarriageClause)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        In contemplation of marriage
      </Link>
    )
  if (willHasIssuesWithCondition)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Issues with will’s condition
      </Link>
    )
  if (willHasCodicilReplacingExecutors)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/internal-checks`}>
        Codicil replacing executors
      </Link>
    )
  if (wasAdopted)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/fact-find#2`}>
        Adopted
      </Link>
    )
  if (taxFormType) probateEstateDetails.push(taxFormType)
  if (grantOrEstateAdmin) probateEstateDetails.push(grantOrEstateAdmin)
  if (applicationType || applicationReferenceNumber)
    probateEstateDetails.push(
      <Link to={`/probate/estates/${probateEstate.id}/probate-application`}>
        {applicationType}
        {applicationReferenceNumber &&
          ` (application reference ${applicationReferenceNumber})`}
      </Link>
    )
  if (hasError)
    probateEstateDetails.push(
      <Link to={`/probate/cases/${probateCaseId}/errors`}>Error logged</Link>
    )

  return probateEstateDetails
}

export const getOriginalCallerContactIfNotOnEstate = (
  probateLead: Lead,
  probateEstatePeople: ProbateEstatePerson[]
): Contact | null => {
  /**
   * The probate lead is removed if they're not an admin or executor.
   * We can see if they are still on the estate by looking for a person
   * with originalContactId - which is only set for the probate lead.
   */
  const originalCallerExistsOnEstate = probateEstatePeople.find(
    (person) => !!person.attributes.originalContactId
  )

  if (!originalCallerExistsOnEstate) return probateLead.attributes.contact

  return null
}

export const caseBookedInvoiceTotalPriceWithVat = (
  invoice: Invoice,
  quotedPrice?: number,
  discount?: number
): number => {
  const totalExclDiscount = invoice.attributes.lineItems.reduce(
    (acc, lineItem) => {
      let basePrice
      if (lineItem.description === 'service_fee' && quotedPrice) {
        basePrice = Dinero({
          amount: quotedPrice,
        })
      } else {
        basePrice = Dinero({
          amount: lineItem.price * (lineItem.quantity || 1),
        })
      }
      const taxRate = lineItem.taxRate ? parseFloat(lineItem.taxRate) : 0
      const taxAmount = basePrice.percentage(taxRate * 100)
      return acc + basePrice.add(taxAmount).toUnit()
    },
    0
  )
  if (discount) {
    return totalExclDiscount - discount / 100
  }

  return totalExclDiscount
}

// Mirrors caseBookedInvoiceTotalPriceWithVat's calculation, but sources the
// disbursement figures from a quote-history row's own disbursementBreakdown
// rather than the (possibly since-patched) invoice — so an older quote's
// total stays consistent with the era-accurate breakdown shown above it.
export const totalPriceFromDisbursementBreakdown = ({
  disbursementBreakdown,
  quotedPrice,
  discount = 0,
}: {
  disbursementBreakdown: DisbursementBreakdownItem[]
  quotedPrice: number
  discount?: number
}): number => {
  const serviceFeeBasePrice = Dinero({ amount: quotedPrice })
  const serviceFeeWithVat = serviceFeeBasePrice
    .add(serviceFeeBasePrice.percentage(VAT_RATE * 100))
    .toUnit()

  const disbursementsTotal = disbursementBreakdown.reduce((acc, item) => {
    const basePrice = Dinero({ amount: item.price * (item.quantity || 1) })
    const taxAmount = basePrice.percentage((item.taxRate || 0) * 100)
    return acc + basePrice.add(taxAmount).toUnit()
  }, 0)

  const totalExclDiscount = serviceFeeWithVat + disbursementsTotal

  return discount ? totalExclDiscount - discount / 100 : totalExclDiscount
}
